import { FC, useContext, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import HubInputField from "src/components/inputType";

import { ENROLLMENT_TIMEZONE, US_STATES } from "src/constants";
import { AddPracticeSchema } from "src/validation/addPractice";
import { ToastTypes } from "src/types";
import { LoaderContext } from "src/context/loaderContext";
import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { AddPracticeFormProps, NPILookupResponse } from "./types";
import ReactInputMask from "react-input-mask";
import { populateAddPracticeForm } from "src/helpers/addPractice";



const AddPractice: FC = (): JSX.Element => {
    const [preservedNpi, updatePreservedNpi] = useState<string>("");

  const [searchParams] = useSearchParams()
  const { setLoader, hideLoader } = useContext(LoaderContext)
  const { showToast } = useContext(ToastContext)

  const { control, formState, handleSubmit, getValues, setValue, reset } = useForm<AddPracticeFormProps>({
    resolver: yupResolver(AddPracticeSchema)
  })




  /**
   * @NPILookup calling npi lookup for details
   * @first useFetch hook for api call
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth npi lookup api trigger
   */
  const { data: npiLookupData, error: npiLookupError, mutate: lookupNpi, removeErrorAndData: clearNpiData } = 
    useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

  /** @third */
  useEffect(() => {
    if (npiLookupData || npiLookupError) {
      hideLoader();
      if(npiLookupData) {
        if (npiLookupData.practice_data !== null) {
          showToast([`Practice Data Found`], ToastTypes.INFO);
          populateAddPracticeForm(setValue, npiLookupData.practice_data, getValues("npi"))
        }

        if (npiLookupData.practice_data === null) {
          reset()
          showToast([`No Data Found`], ToastTypes.ERROR);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupData, npiLookupError]);

  /** @fourth */
  const callNPILookup = () => {
    const npi = getValues("npi");

    if (!npiLookupData || npi !== preservedNpi) {
      const npiError = formState.errors && formState.errors.npi;

      if (
        !npi || 
        (npi && npi.length === 0) || 
        npiError
      )
        showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
      else {
        setLoader();
        clearNpiData();
        updatePreservedNpi(npi);
        const data = {
          'global_doctor[npi]': npi,
        };
        lookupNpi(null, null, data);
      }
    }
  };




  const { loading: addPracticeLoader, data: addPracticeRes, mutate: addNewPractice } = 
    useFetch<{ practice_id: number; status: number; success: boolean; }>(APIEndpoints.ADD_PRACTICE)

  useEffect(() => {
    if (addPracticeRes) {
      const { status, success } = addPracticeRes
      if (status === 200 ||success)
        reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPracticeRes])

  const submitPracticeForm = (values: AddPracticeFormProps) => {
    const title = searchParams.get("title");
    let practice_type; 
    if (title) {
      if (title.includes("Referring"))
        practice_type = "referring"
      else
        practice_type = "treating"
    }
    const { phone_number, ...practiceValues } = values;
    const submittingValues = {
      ...practiceValues,
      practice_type,
      phone_number: {
        number: values.phone_number,
        phone_type: "work",
      },
    }

    addNewPractice(submittingValues)
  }


  return (
    <div className="container-fluid d-flex flex-fill auth-page body-bg overflow-auto pt-2">
      <div className="container d-flex flex-column">
        <AuthSubHeader />

        <div className="card mt-4">
          <div className="card-header">
            <h4 className="mb-0">{searchParams.get("title") ? searchParams.get("title") : "Add Practice"}</h4>
          </div>

          <div className="card-body">
            <p>Please input the information for the provider you wish to create below. The provider will
              be
              automatically
              added to all practices attached to your account, and available immediately for use in
              new
              requests.</p>

            <form onSubmit={handleSubmit(submitPracticeForm)}>
              <Controller
                name="npi"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="npi"
                    inputLabel="NPI"
                    isRequired={true}
                    isRowBased={true}
                    inputPlaceholder="NPI should be 10 digits"
                    field={field}
                    error={error}
                    onBlur={callNPILookup}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        callNPILookup();
                      }
                    }}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <HubInputField
                    inputId="name"
                    inputLabel="Practice Name"
                    inputPlaceholder="Specify Practice Name"
                    isRequired={true}
                    isRowBased={true}
                    field={field}
                    error={error}
                  />
                )}
              />

              <div className="row">
                <div className="col-md-6">
                  <Controller
                    name="office_contact"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="office_contact"
                        inputLabel="Office Contact"
                        inputPlaceholder="Specify Contact Name"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="tax_id"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="tax"
                        inputLabel="Tax ID"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="phone_number"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <div className="row">
                        <label htmlFor="phone_number" className="form-label col-lg-4 col-md-4 col-12">
                          Phone Number *
                        </label>
                        <div className="col-lg-8 col-md-8 col-12">
                          <ReactInputMask
                            {...field}
                            mask="(999) 999-9999"
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                          >
                            {(inputProps: {}) => <input id="enrollment_practices_attributes.0.phone_number" type="text" className="form-control" placeholder="(555) 555-5555" {...inputProps} />}
                          </ReactInputMask>

                          {error && error.message ? (
                            <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="fax_number"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <div className="row mb-2">
                        <label htmlFor="phone_number" className="form-label col-lg-4 col-md-4 col-12">
                          Fax Number *
                        </label>
                        <div className="col-lg-8 col-md-8 col-12">
                          <ReactInputMask
                            {...field}
                            mask="(999) 999-9999"
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                          >
                            {(inputProps: {}) => <input id="enrollment_practices_attributes.0.phone_number" type="text" className="form-control" placeholder="(555) 555-5555" {...inputProps} />}
                          </ReactInputMask>

                          {error && error.message ? (
                            <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-md-6">
                  <Controller
                    name="address.street"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.street"
                        inputLabel="Street"
                        inputPlaceholder="Street"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.suite"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.suite"
                        inputLabel="Suite"
                        inputPlaceholder="Suite"
                        isRequired={false}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-5">
                  <Controller
                    name="address.city"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <div className="row">
                        <div className="col-md-5">
                          <label className="form-label text-end p-2" htmlFor="city"> City * </label>
                        </div>

                        <div className="col-md-7 ps-1">
                          <HubInputField
                            inputId="address.city"
                            inputPlaceholder="City"
                            isRequired={true}
                            isRowBased={true}
                            field={field}
                            error={error}
                          />
                        </div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-lg-4">
                  <Controller
                    name="address.state"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        field={field}
                        error={error}
                        inputId="address.state"
                        inputPlaceholder="Select State..."
                        inputLabel="State"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        isDropdown={true}
                        isSearchable={false}
                        dropdownData={US_STATES as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-3">
                  <Controller
                    name="address.zip"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.zip"
                        inputPlaceholder="Zip"
                        inputLabel="Zip"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="col-lg-6">
                  <Controller
                    name="address.timezone"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <HubInputField
                        inputId="address.timezone"
                        inputLabel="Timezone"
                        isRequired={true}
                        isRowBased={true}
                        isRowInsideAnother={true}
                        field={field}
                        error={error}
                        isDropdown={true}
                        isSearchable={false}
                        dropdownData={ENROLLMENT_TIMEZONE as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      />
                    )}
                  />
                </div>
              </div>

              <button type="submit" 
                disabled={addPracticeLoader}
                className="btn btn-primary mt-2 float-end"
              >
                {addPracticeLoader ? (
                  <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                ) : null} <i className="bi bi-send-fill"></i>
                <span className="d-block d-lg-inline-block ps-2">Add Practice</span>
              </button>
            </form>
          </div>
        </div>

        <div className="mt-3"><FooterText footerType="black" /></div>
      </div>
    </div>
  )
}

export default AddPractice;
